import { Area, AreaChart, CartesianGrid, XAxis, YAxis, Tooltip, TooltipProps, ReferenceLine } from 'recharts';
import format from 'date-fns/format';
import { useMemo, useState } from 'react';
import { formatAsPreciseWithDecimalsPct, pointsToPrecisePctWithDecimals } from '@silo/utils';

export type ApyChartData = Props['data'];

type Props = {
  data: {
    depositApy?: number;
    borrowApy?: number;
    date: Date;
  }[];
  hourly?: boolean;
  averageAPY?: number;
};

export const ApyChart = (props: Props) => {
  // treat as brand new chart on data changes
  const key = useMemo(() => Math.random() + props.data.length, [props.data]);

  return <Chart key={key} {...props} />;
};

/**
 * TODO: figure out / implement proper usage of the tailwind config to grab
 * these theme values instead of hard-coding them
 */
const Chart = (props: Props) => {
  const { data, averageAPY } = props;
  const [yAxisWidth, setYAxisWidth] = useState<number | undefined>(undefined);

  return (
    <div>
      <AreaChart width={630} height={270} data={data}>
        <defs>
          <linearGradient id="colorBorrow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#A78BFA" stopOpacity={1} />
            <stop offset="145%" stopColor="#A78BFA" stopOpacity={0.5} />
          </linearGradient>
          <linearGradient id="colorDeposit" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#A78BFA" stopOpacity={1} />
            <stop offset="145%" stopColor="#A78BFA" stopOpacity={0.5} />
          </linearGradient>
        </defs>

        <XAxis
          dataKey="date"
          strokeDasharray="3 3"
          tick={{ fill: '#B0B8C4', fontSize: '12px', fontFamily: 'DM Mono' }}
          tickMargin={28}
          tickFormatter={(value) => {
            if (!(value instanceof Date)) return value;

            return props.hourly ? format(value, 'HH:mm') : format(value, 'MMM dd');
          }}
          height={45}
        />
        <YAxis
          strokeWidth={0}
          tick={{ fill: '#B0B8C4', fontSize: '12px', fontFamily: 'DM Mono' }}
          width={yAxisWidth}
          tickFormatter={(v) => {
            const formatted = formatAsPreciseWithDecimalsPct(v / 100);

            /**
             * recharts doesn't support responsive axes widths.
             * so the idea here is to determine what the width of y axis should be.
             * the most precise way found so far is to use the actual ticks that recharts
             * intends to show - and calculate the width based on the widest tick.
             */
            // 9px for each character
            // + 1, because we also display a "%" sign in the tick
            const axisWidth = (formatted.length + 1) * 9;

            if (!yAxisWidth || axisWidth > yAxisWidth) {
              setYAxisWidth(axisWidth);
            }

            return formatted;
          }}
        />
        <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#334155" />
        <Tooltip content={<CustomTooltip hourly={props.hourly} />} />
        <Area
          type="linear"
          dataKey="borrowApy"
          stroke="#A78BFA"
          strokeWidth={2}
          fillOpacity={0}
          fill="url(#colorBorrow)"
          name="Borrow APR"
        />
        <Area
          type="linear"
          dataKey="depositApy"
          stroke="#A78BFA"
          strokeWidth={2}
          fillOpacity={0}
          fill="url(#colorDeposit)"
          name="Deposit APR"
        />
        {averageAPY ? <ReferenceLine y={averageAPY} stroke="#F8FAFC" strokeDasharray="3 3" strokeWidth={2} /> : null}
      </AreaChart>
    </div>
  );
};

const CustomTooltip = (props: TooltipProps<any, any> & { hourly?: boolean }) => {
  const { active, label, payload, hourly } = props;
  if (!active || !(label instanceof Date) || !payload || !payload.every((p) => typeof p.value === 'number'))
    return null;

  const date = hourly ? format(label, 'MMM dd, HH:mm') : format(label, 'MMM dd');
  const depositApy = payload.find((value) => value.dataKey === 'depositApy');
  const borrowApy = payload.find((value) => value.dataKey === 'borrowApy');

  return (
    <div className="px-3 py-2 font-sans text-xs rounded-lg shadow-md bg-shale-750 text-shale-100">
      <p>{date}</p>
      {depositApy ? (
        <p>
          Deposit APR: <span className="font-medium">{pointsToPrecisePctWithDecimals(depositApy.value)}</span>
        </p>
      ) : null}
      {borrowApy ? (
        <p>
          Borrow APR: <span className="font-medium">{pointsToPrecisePctWithDecimals(borrowApy.value)}</span>
        </p>
      ) : null}
    </div>
  );
};
